import { CheckCircle2, Clock3 } from "lucide-react";

/**
 * AvailabilityBadge — premium urgency indicator for vehicle delivery timing.
 *
 * Props:
 *   car        – car object with `available_immediately` / `available_short_term`
 *   size       – "lg" (CarDetail hero/sidebar) | "sm" (LandingPage card)
 *   className  – extra wrapper classes
 */
export function AvailabilityBadge({ car, size = "lg", className = "" }) {
  if (!car) return null;
  const immediate = !!car.available_immediately;
  const shortTerm = !!car.available_short_term;
  if (!immediate && !shortTerm) return null;

  // Small (listing card) variant
  if (size === "sm") {
    if (immediate) {
      return (
        <div
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-[10px] font-semibold backdrop-blur-sm ${className}`}
          data-testid="availability-badge-immediate-small"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span>Azonnal átvehető</span>
        </div>
      );
    }
    return (
      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-[10px] font-semibold backdrop-blur-sm ${className}`}
        data-testid="availability-badge-short-small"
      >
        <Clock3 className="w-2.5 h-2.5" />
        <span>2–3 napon belül</span>
      </div>
    );
  }

  // Large (CarDetail) variant — glow + soft pulse, premium automotive look
  if (immediate) {
    return (
      <div
        className={`relative overflow-hidden rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent border border-emerald-400/40 shadow-[0_0_30px_-8px_rgba(16,185,129,0.5)] ${className}`}
        data-testid="availability-badge-immediate"
      >
        {/* animated glow */}
        <div className="pointer-events-none absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-emerald-400/10 blur-2xl animate-pulse" />
        </div>
        <div className="relative flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-500/25 border border-emerald-400/50 flex items-center justify-center shadow-[0_0_12px_rgba(16,185,129,0.5)] animate-pulse">
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm sm:text-base font-bold text-emerald-200 uppercase tracking-wider">Azonnal átvehető</span>
              <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-[10px] font-semibold text-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" /> Élő készlet
              </span>
            </div>
            <p className="text-xs text-emerald-100/70 leading-relaxed">
              Az autó azonnal vagy nagyon rövid időn belül átvehető.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Short-term variant — warmer amber palette, softer animation
  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent border border-amber-400/40 shadow-[0_0_24px_-10px_rgba(245,158,11,0.5)] ${className}`}
      data-testid="availability-badge-short"
    >
      <div className="relative flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500/25 border border-amber-400/50 flex items-center justify-center shadow-[0_0_10px_rgba(245,158,11,0.4)]">
          <Clock3 className="w-5 h-5 text-amber-300" />
        </div>
        <div className="min-w-0">
          <span className="block text-sm sm:text-base font-bold text-amber-200 uppercase tracking-wider mb-0.5">
            Rövid időn belül átvehető
          </span>
          <p className="text-xs text-amber-100/70 leading-relaxed">
            Az autó várhatóan néhány napon belül átvehető.
          </p>
        </div>
      </div>
    </div>
  );
}
